import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Download } from "lucide-react";
import { type AccountSummary, useAccountEvents } from "@/lib/accounts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Alert } from "@/components/ui/alert";
import { pushToast } from "@/components/Toaster";
import { formatDateTime } from "@/lib/format";

const STATUS_TONE: Record<string, string> = {
  done: "text-success",
  added: "text-success",
  failed: "text-danger",
  cancelled: "text-fg-muted",
  skipped: "text-fg-muted",
  pending: "text-warning",
  running: "text-warning",
  queued: "text-warning",
};

function statusClass(status: string): string {
  return STATUS_TONE[status] ?? "text-warning";
}

/**
 * AccountLogsTab — the former AccountEventsBlock, now a tab. Sub-tabs (Warmup /
 * Invites / Scrapes / Audit) with counts as badges; the events query stays
 * enabled only while the drawer/tab is mounted.
 */
export function AccountLogsTab({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const events = useAccountEvents(account.id, true);
  const [sub, setSub] = useState("warmup");

  const download = () => {
    if (!events.data) return;
    const blob = new Blob([JSON.stringify(events.data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName =
      account.tg_username ??
      (account.tg_user_id != null ? String(account.tg_user_id) : account.id);
    a.download = `account-${safeName}-events.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copy = async () => {
    if (!events.data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(events.data, null, 2));
      pushToast({ kind: "success", text: t("accounts.events.copied") });
    } catch {
      pushToast({ kind: "error", text: t("accounts.events.copy_failed") });
    }
  };

  const d = events.data;

  return (
    <div className="space-y-4">
      <p className="text-xs text-fg-muted">{t("accounts.events.hint")}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={events.isLoading || !d}
          onClick={download}
          iconLeft={<Download size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {events.isLoading ? t("accounts.events.loading") : t("accounts.events.download")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={events.isLoading || !d}
          onClick={() => void copy()}
          iconLeft={<Copy size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("accounts.events.copy")}
        </Button>
      </div>

      {events.error && (
        <Alert tone="danger">
          {events.error instanceof Error ? events.error.message : t("accounts.error_unknown")}
        </Alert>
      )}

      {d && (
        <Tabs value={sub} onValueChange={setSub} variant="pill">
          <TabsList>
            <TabsTrigger value="warmup">
              {t("accounts.events.warmup_section")}
              <Badge tone="neutral" className="ml-1.5">{d.warmup_actions.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="invites">
              {t("accounts.events.invites_section")}
              <Badge tone="neutral" className="ml-1.5">{d.invite_attempts.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="scrapes">
              {t("accounts.events.scrapes_section")}
              <Badge tone="neutral" className="ml-1.5">{d.scrape_jobs.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="audit">
              {t("accounts.events.audit_section")}
              <Badge tone="neutral" className="ml-1.5">{d.audit_events.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="warmup">
            {d.warmup_actions.length === 0 ? (
              <Empty text={t("accounts.events.empty")} />
            ) : (
              <ScrollTable>
                <THead>
                  <TR>
                    <TH>{t("accounts.events.col.stage")}</TH>
                    <TH>{t("accounts.events.col.action")}</TH>
                    <TH>{t("accounts.events.col.status")}</TH>
                    <TH>{t("accounts.events.col.when")}</TH>
                    <TH>{t("accounts.events.col.error")}</TH>
                  </TR>
                </THead>
                <TBody>
                  {d.warmup_actions.map((row) => {
                    const when = row.executed_at ?? row.scheduled_at;
                    return (
                      <TR key={row.id} className="align-top">
                        <TD className="text-xs">{row.stage}</TD>
                        <TD className="whitespace-nowrap text-xs">
                          {t(`warmup.action.${row.action_type}`, {
                            defaultValue: row.action_type,
                          })}
                        </TD>
                        <TD className={`text-xs ${statusClass(row.status)}`}>{row.status}</TD>
                        <TD className="whitespace-nowrap text-xs text-fg-muted">
                          {when ? formatDateTime(when) : "—"}
                        </TD>
                        <TD className="break-all text-xs text-danger">{row.error ?? ""}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </ScrollTable>
            )}
          </TabsContent>

          <TabsContent value="invites">
            {d.invite_attempts.length === 0 ? (
              <Empty text={t("accounts.events.empty")} />
            ) : (
              <ScrollTable>
                <THead>
                  <TR>
                    <TH>{t("accounts.events.col.target_user")}</TH>
                    <TH>{t("accounts.events.col.target_channel")}</TH>
                    <TH>{t("accounts.events.col.status")}</TH>
                    <TH>{t("accounts.events.col.when")}</TH>
                    <TH>{t("accounts.events.col.error")}</TH>
                  </TR>
                </THead>
                <TBody>
                  {d.invite_attempts.map((row) => {
                    const when = row.attempted_at ?? row.created_at;
                    return (
                      <TR key={row.id} className="align-top">
                        <TD className="font-mono text-xs">{row.target_tg_user_id}</TD>
                        <TD className="text-xs">{row.target_channel_title ?? "—"}</TD>
                        <TD className={`text-xs ${statusClass(row.status)}`}>{row.status}</TD>
                        <TD className="whitespace-nowrap text-xs text-fg-muted">
                          {formatDateTime(when)}
                        </TD>
                        <TD className="break-all text-xs text-danger">{row.error ?? ""}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </ScrollTable>
            )}
          </TabsContent>

          <TabsContent value="scrapes">
            {d.scrape_jobs.length === 0 ? (
              <Empty text={t("accounts.events.empty")} />
            ) : (
              <ScrollTable>
                <THead>
                  <TR>
                    <TH>{t("accounts.events.col.channel")}</TH>
                    <TH>{t("accounts.events.col.status")}</TH>
                    <TH className="text-right">{t("accounts.events.col.users_added")}</TH>
                    <TH className="text-right">{t("accounts.events.col.users_seen")}</TH>
                    <TH>{t("accounts.events.col.when")}</TH>
                    <TH>{t("accounts.events.col.error")}</TH>
                  </TR>
                </THead>
                <TBody>
                  {d.scrape_jobs.map((row) => {
                    const when = row.finished_at ?? row.started_at ?? row.created_at;
                    return (
                      <TR key={row.id} className="align-top">
                        <TD className="font-mono text-xs">
                          {row.channel_label ?? row.channel_id.slice(0, 8)}
                        </TD>
                        <TD className={`text-xs ${statusClass(row.status)}`}>{row.status}</TD>
                        <TD className="text-right font-mono text-xs">{row.users_added}</TD>
                        <TD className="text-right font-mono text-xs text-fg-muted">
                          {row.users_seen}
                        </TD>
                        <TD className="whitespace-nowrap text-xs text-fg-muted">
                          {formatDateTime(when)}
                        </TD>
                        <TD className="break-all text-xs text-danger">{row.error ?? ""}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </ScrollTable>
            )}
          </TabsContent>

          <TabsContent value="audit">
            {d.audit_events.length === 0 ? (
              <Empty text={t("accounts.events.empty")} />
            ) : (
              <ScrollTable>
                <THead>
                  <TR>
                    <TH>{t("accounts.events.col.action")}</TH>
                    <TH>{t("accounts.events.col.when")}</TH>
                    <TH>{t("accounts.events.col.details")}</TH>
                  </TR>
                </THead>
                <TBody>
                  {d.audit_events.map((row) => (
                    <TR key={row.id} className="align-top">
                      <TD className="font-mono text-xs">{row.action}</TD>
                      <TD className="whitespace-nowrap text-xs text-fg-muted">
                        {formatDateTime(row.created_at)}
                      </TD>
                      <TD className="break-all font-mono text-xs text-fg-muted">
                        {JSON.stringify(row.details)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </ScrollTable>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ScrollTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-h-72 overflow-auto rounded-xl border border-border">
      <Table>{children}</Table>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="py-6 text-center text-sm text-fg-muted">{text}</p>;
}
